import PortfolioProjectGanttCard, { type PortfolioGanttProjectPayload } from '@/components/features/Projects/PortfolioProjectGanttCard';
import { projectFilterOptions, projectTableDescriptor } from '@/components/features/Projects/tables/project-table-descriptor';
import { ProjectCard } from '@/components/features/Projects/tables/ProjectCard';
import { ProjectTable } from '@/components/features/Projects/tables/ProjectTable';
import { CreateButton } from '@/components/layout/CreateButton';
import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import {
    buildToolbarDefinitions,
    columnIdForServerSortKey,
    quickFilterButtonLabel,
    serverSortKey,
    toFilterSpecMap,
} from '@/components/ui/embedded-resource-table/descriptor';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu';
import { Label } from '@/components/ui/shadcn/label';
import { MultiSelect } from '@/components/ui/shadcn/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { useSettings } from '@/contexts/SettingsContext';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs';
import { useCachedFetch } from '@/hooks/use-cached-fetch';
import { useColumnVisibility } from '@/hooks/use-column-visibility';
import { useDebounce } from '@/hooks/use-debounce';
import { useIndexFilters } from '@/hooks/use-index-filters';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { assignableUserId, type AssignableUser } from '@/lib/assigned-user-options';
import { PROJECT_COUNTRY_TYPE_OPTIONS } from '@/lib/constants';
import { settingLabel } from '@/lib/status-labels';
import { useCan } from '@/lib/utils';
import { Company, PaginationMeta, Project, User } from '@/types';
import type { PageProps as InertiaPageProps } from '@inertiajs/core';
import { Head, router, usePage } from '@inertiajs/react';
import axios from 'axios';
import { CalendarRange, ChevronDown, FileSpreadsheet } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

const EXPORT_GANTT_ALL = 'all';
const EXPORT_GANTT_SORT_OWNER = 'owner';
const EXPORT_GANTT_SORT_PROJECT_NUMBER = 'project_number';
const GANTT_PORTFOLIO_PAGE_SIZE = 10;

const COLUMN_STORAGE_KEY = 'projects:index';

interface ProjectPageProps extends InertiaPageProps {
    projects: Project[];
    pagination: PaginationMeta;
    filters: Record<string, string | number | undefined>;
    availableYears?: number[];
    /** Every active colleague, with their roles — what the people filters group by. */
    filterUsers?: AssignableUser[];
    auth?: {
        user: User | null;
        roles?: string[];
    };
}

export default function Index() {
    const { props } = usePage<ProjectPageProps>();
    const { projects, pagination, filters, availableYears, filterUsers = [], auth } = props;
    const { t } = useTranslations();
    const { settings } = useSettings();
    const { can } = useCan();
    const canChangeStatus = can('projects.status.change');
    const breadcrumbs = useBreadcrumbs('projects');
    const currentYear = new Date().getFullYear().toString();

    // Columns, filters and sort entries come from the descriptor the detail-page
    // tabs read too, so the four renderings of this table cannot drift apart.
    const definitions = useMemo(() => buildToolbarDefinitions(projectTableDescriptor, t, { requireServerSort: true }), [t]);
    const filterSpecs = useMemo(() => toFilterSpecMap(projectTableDescriptor), []);

    const {
        columnFilters,
        handleFilterChange,
        toggleSingleFilter,
        clearFilters: clearServerFilters,
        applyFilters,
        loading,
    } = useIndexFilters(filterSpecs, filters, '/projects');

    // Filter dropdown data is cached in-memory (see useCachedFetch) so re-opening the page or the
    // Filters modal is instant instead of re-fetching and popping the Company card in late.
    const { data: companiesData, loading: companiesLoading } = useCachedFetch<{ companies: Company[] }>('/api/companies/dropdown');

    const companies = useMemo(() => companiesData?.companies ?? [], [companiesData]);
    // The people filters read the server's list rather than /api/users/assignable:
    // that endpoint needs assign-employees, so an engineer used to get two empty
    // cards on a page they are otherwise allowed to use.
    const users = filterUsers;

    // Tell the toolbar which filters are still fetching their options, so their cards render a
    // spinner instead of staying hidden until the async data lands.
    const columnFilterLoading = useMemo<Record<string, boolean>>(() => ({ company: companiesLoading }), [companiesLoading]);

    const years = useMemo(() => {
        if (availableYears && availableYears.length > 0) {
            return availableYears;
        }
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 10 }, (_, i) => currentYear - i);
    }, [availableYears]);

    const statuses = useMemo(() => settings.projectStatuses, [settings.projectStatuses]);

    const isMobile = useIsMobile();
    const [viewMode, setViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [searchValue, setSearchValue] = useState(filters.search?.toString() ?? '');
    const debouncedSearchValue = useDebounce(searchValue, 800);
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    // `filters.sort_by` is a server key ('contract_number'); the toolbar indicator wants
    // the column id ('name') it belongs to.
    const [sortColumn, setSortColumn] = useState<string | null>(
        columnIdForServerSortKey(projectTableDescriptor, filters.sort_by?.toString()) ?? null,
    );
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
        filters.sort_order === 'asc' || filters.sort_order === 'desc' ? filters.sort_order : null,
    );
    const {
        visibleColumns,
        showColumn: handleColumnShow,
        hideColumn: handleColumnHide,
    } = useColumnVisibility(COLUMN_STORAGE_KEY, definitions.defaultVisibleColumns, definitions.allColumnIds);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [exportGanttOpen, setExportGanttOpen] = useState(false);
    const [exportProjectStatuses, setExportProjectStatuses] = useState<string[]>([]);
    const [exportTaskStatuses, setExportTaskStatuses] = useState<string[]>([]);
    const [exportTimelineYears, setExportTimelineYears] = useState<string[]>([currentYear]);
    const [exportIncludeAllTimelineRows, setExportIncludeAllTimelineRows] = useState(false);
    const [exportGanttAssignee, setExportGanttAssignee] = useState<string>(EXPORT_GANTT_ALL);
    const [exportGanttSort, setExportGanttSort] = useState<string>(EXPORT_GANTT_SORT_OWNER);

    const [ganttPortfolioProjects, setGanttPortfolioProjects] = useState<PortfolioGanttProjectPayload[]>([]);
    const [ganttPortfolioPage, setGanttPortfolioPage] = useState(1);
    const [ganttPortfolioMeta, setGanttPortfolioMeta] = useState({
        total: 0,
        current_page: 1,
        per_page: GANTT_PORTFOLIO_PAGE_SIZE,
        last_page: 1,
    });
    const portfolioGanttFilterSigRef = useRef<string>('');
    const ganttPortfolioLoadRunRef = useRef(0);
    const [ganttPortfolioLoading, setGanttPortfolioLoading] = useState(false);
    const [ganttPortfolioError, setGanttPortfolioError] = useState<string | null>(null);
    const [ganttProjectStatusesFilter, setGanttProjectStatusesFilter] = useState<string[]>([]);
    const [ganttTaskStatusesFilter, setGanttTaskStatusesFilter] = useState<string[]>([]);
    const [ganttCountryTypesFilter, setGanttCountryTypesFilter] = useState<string[]>([]);
    const [ganttAssigneesFilter, setGanttAssigneesFilter] = useState<string[]>([]);

    useEffect(() => {
        setViewMode((current) => {
            if (isMobile && current === 'table') {
                return 'grid';
            }
            return current;
        });
    }, [isMobile]);

    useEffect(() => {
        const nextSearch = filters.search?.toString() ?? '';
        setSearchValue((previous) => (previous === nextSearch ? previous : nextSearch));
    }, [filters.search]);

    useEffect(() => {
        if ((filters.search?.toString() ?? '') === debouncedSearchValue) {
            return;
        }

        applyFilters({
            search: debouncedSearchValue || undefined,
            page: 1,
        });
    }, [applyFilters, debouncedSearchValue, filters.search]);

    const companyOptions = useMemo(() => {
        if (!companies) return [];
        return companies
            .map((company) => ({
                value: (company.id || (company as Company).uuid) as string,
                name: company.name,
            }))
            .filter((company) => company.name)
            .map((company) => ({ id: company.value, name: company.name }));
    }, [companies]);

    const timelineYearOptions = useMemo(() => {
        return years.map((year) => ({
            value: year.toString(),
            label: year.toString(),
        }));
    }, [years]);
    const selectedTimelineYear = columnFilters.timeline_year?.[0] ?? currentYear;
    const selectedTimelineYears = columnFilters.timeline_year ?? [];
    const timelineYearButtonLabel =
        selectedTimelineYears.length === 0
            ? t('projects.index.filters.all_years', 'All')
            : [...selectedTimelineYears].sort((a, b) => Number(b) - Number(a)).join(', ');

    // Region labels used to come straight from config.json, which is English only.
    const countryTypeOptions = useMemo(
        () =>
            PROJECT_COUNTRY_TYPE_OPTIONS.map((option) => ({
                value: option.value,
                label: settingLabel(t, 'project_country_types', option.value) || option.label,
            })),
        [t],
    );

    const projectStatusExportOptions = useMemo(
        () => settings.projectStatuses.map((s) => ({ value: s.value, label: settingLabel(t, 'project_statuses', s.value) })),
        [settings.projectStatuses, t],
    );

    const taskStatusExportOptions = useMemo(
        () => settings.projectTaskStatuses.map((s) => ({ value: s.value, label: settingLabel(t, 'project_task_statuses', s.value) })),
        [settings.projectTaskStatuses, t],
    );

    const isAdministrator = useMemo(() => Array.isArray(auth?.roles) && auth.roles.includes('Administrator'), [auth?.roles]);

    const exportGanttAssigneeOptions = useMemo(() => {
        const currentUserId = auth?.user?.id;
        const items: { value: string; label: string }[] = [
            { value: EXPORT_GANTT_ALL, label: t('projects.index.export_gantt.assignee_all', 'Everyone') },
        ];
        if (currentUserId) {
            items.push({
                value: currentUserId,
                label: t('projects.index.export_gantt.assignee_me', 'Me'),
            });
        }
        if (isAdministrator && users.length > 0) {
            const seen = new Set(items.map((i) => i.value));
            const sorted = [...users].sort((a, b) => {
                const an = `${a.first_name} ${a.last_name}`.trim().toLowerCase();
                const bn = `${b.first_name} ${b.last_name}`.trim().toLowerCase();
                return an.localeCompare(bn);
            });
            for (const u of sorted) {
                const id = assignableUserId(u);
                if (id === '' || seen.has(id)) {
                    continue;
                }
                seen.add(id);
                items.push({
                    value: id,
                    label: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || id,
                });
            }
        }
        return items;
    }, [auth?.user?.id, isAdministrator, t, users]);

    const ganttAssigneeMultiOptions = useMemo(
        () =>
            users
                .map((user) => ({
                    value: assignableUserId(user),
                    label: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                }))
                .filter((option) => option.value !== '' && option.label !== ''),
        [users],
    );

    const ganttProjectStatusMultiOptions = useMemo(() => statuses.map((s) => ({ value: s.value, label: s.label })), [statuses]);

    const ganttTaskStatusMultiOptions = useMemo(
        () => settings.projectTaskStatuses.map((s) => ({ value: s.value, label: s.label })),
        [settings.projectTaskStatuses],
    );

    const ganttCountryTypeMultiOptions = useMemo(() => countryTypeOptions.map((o) => ({ value: o.value, label: o.label })), [countryTypeOptions]);

    const hasConfiguredGanttFilters = useMemo(() => {
        return (
            ganttProjectStatusesFilter.length > 0 ||
            ganttTaskStatusesFilter.length > 0 ||
            ganttCountryTypesFilter.length > 0 ||
            ganttAssigneesFilter.length > 0 ||
            debouncedSearchValue.trim() !== ''
        );
    }, [
        debouncedSearchValue,
        ganttAssigneesFilter.length,
        ganttCountryTypesFilter.length,
        ganttProjectStatusesFilter.length,
        ganttTaskStatusesFilter.length,
    ]);

    const resolvedGanttProjectStatuses = useMemo(() => {
        if (ganttProjectStatusesFilter.length > 0) {
            return ganttProjectStatusesFilter;
        }
        return settings.projectStatuses.filter((s) => s.value !== 'cancelled').map((s) => s.value);
    }, [ganttProjectStatusesFilter, settings.projectStatuses]);

    const resolvedGanttTaskStatuses = useMemo(() => {
        if (ganttTaskStatusesFilter.length > 0) {
            return ganttTaskStatusesFilter;
        }
        return settings.projectTaskStatuses.map((s) => s.value);
    }, [ganttTaskStatusesFilter, settings.projectTaskStatuses]);

    const ganttFetchPayload = useMemo(
        () => ({
            year: selectedTimelineYear,
            search: debouncedSearchValue,
            projectStatuses: resolvedGanttProjectStatuses.slice().sort(),
            taskStatuses: resolvedGanttTaskStatuses.slice().sort(),
            countryTypes: ganttCountryTypesFilter.slice().sort(),
            assignees: ganttAssigneesFilter.slice().sort(),
        }),
        [
            selectedTimelineYear,
            debouncedSearchValue,
            resolvedGanttProjectStatuses,
            resolvedGanttTaskStatuses,
            ganttCountryTypesFilter,
            ganttAssigneesFilter,
        ],
    );

    const ganttFetchSignature = useMemo(() => JSON.stringify(ganttFetchPayload), [ganttFetchPayload]);

    const debouncedGanttFetchSignature = useDebounce(ganttFetchSignature, 300);

    useEffect(() => {
        if (viewMode !== 'gantt') {
            return;
        }

        if (!hasConfiguredGanttFilters) {
            portfolioGanttFilterSigRef.current = '';
            ganttPortfolioLoadRunRef.current += 1;
            setGanttPortfolioProjects((previous) => (previous.length === 0 ? previous : []));
            setGanttPortfolioMeta({
                total: 0,
                current_page: 1,
                per_page: GANTT_PORTFOLIO_PAGE_SIZE,
                last_page: 1,
            });
            setGanttPortfolioPage(1);
            setGanttPortfolioError((previous) => (previous === null ? previous : null));
            setGanttPortfolioLoading((previous) => (previous === false ? previous : false));
            return;
        }

        const payload = JSON.parse(debouncedGanttFetchSignature) as {
            year: string;
            search: string;
            projectStatuses: string[];
            taskStatuses: string[];
            countryTypes: string[];
            assignees: string[];
        };

        const controller = new AbortController();
        let cancelled = false;

        const loadPortfolioGantt = async () => {
            const runId = ++ganttPortfolioLoadRunRef.current;
            setGanttPortfolioLoading(true);
            setGanttPortfolioError(null);

            try {
                const params = new URLSearchParams();
                params.set('timeline_year', payload.year);
                payload.projectStatuses.forEach((status) => params.append('project_statuses[]', status));
                payload.taskStatuses.forEach((status) => params.append('task_statuses[]', status));
                payload.countryTypes.forEach((countryType) => params.append('country_types[]', countryType));
                payload.assignees.forEach((assignee) => params.append('assigned_to[]', assignee));

                if (payload.search.trim() !== '') {
                    params.set('search', payload.search.trim());
                }

                const filterChanged = portfolioGanttFilterSigRef.current !== debouncedGanttFetchSignature;
                portfolioGanttFilterSigRef.current = debouncedGanttFetchSignature;
                const pageToRequest = filterChanged ? 1 : ganttPortfolioPage;
                if (filterChanged && ganttPortfolioPage !== 1) {
                    setGanttPortfolioPage(1);
                }
                params.set('page', String(pageToRequest));
                params.set('per_page', String(GANTT_PORTFOLIO_PAGE_SIZE));

                const response = await axios.get<{
                    projects: PortfolioGanttProjectPayload[];
                    meta: {
                        total: number;
                        current_page?: number;
                        per_page?: number;
                        last_page?: number;
                    };
                }>(`${route('projects.gantt-data')}?${params.toString()}`, { signal: controller.signal });

                if (cancelled || runId !== ganttPortfolioLoadRunRef.current) {
                    return;
                }

                const projects = response.data.projects ?? [];
                const meta = response.data.meta ?? { total: projects.length };
                const perPage = meta.per_page ?? GANTT_PORTFOLIO_PAGE_SIZE;
                const total = meta.total ?? projects.length;
                const lastPage = Math.max(1, meta.last_page ?? Math.ceil(total / Math.max(1, perPage)));
                const currentPage = meta.current_page ?? pageToRequest;
                setGanttPortfolioProjects(projects);
                setGanttPortfolioMeta({
                    total,
                    current_page: currentPage,
                    per_page: perPage,
                    last_page: lastPage,
                });
            } catch (error) {
                const isCancelledRequest = cancelled || controller.signal.aborted || (axios.isAxiosError(error) && error.code === 'ERR_CANCELED');

                if (isCancelledRequest || runId !== ganttPortfolioLoadRunRef.current) {
                    return;
                }

                setGanttPortfolioProjects([]);
                setGanttPortfolioMeta({
                    total: 0,
                    current_page: 1,
                    per_page: GANTT_PORTFOLIO_PAGE_SIZE,
                    last_page: 1,
                });
                setGanttPortfolioError(t('projects.index.portfolio_gantt.load_failed', 'Could not load Gantt data.'));
            } finally {
                if (cancelled || runId === ganttPortfolioLoadRunRef.current) {
                    setGanttPortfolioLoading(false);
                }
            }
        };

        void loadPortfolioGantt();

        return () => {
            cancelled = true;
            controller.abort();
            ganttPortfolioLoadRunRef.current += 1;
        };
    }, [viewMode, hasConfiguredGanttFilters, debouncedGanttFetchSignature, ganttPortfolioPage, t]);

    const openExportGanttDialog = () => {
        // Preselect only ongoing statuses by default — exclude terminal/closed projects
        // (completed, cancelled). Users can still add them back in the picker.
        setExportProjectStatuses(settings.projectStatuses.filter((s) => s.value !== 'cancelled' && s.value !== 'completed').map((s) => s.value));
        setExportTaskStatuses(settings.projectTaskStatuses.map((s) => s.value));
        // Default to no year selected, which means "all years included". As soon as the
        // user picks specific years, the export is narrowed to those years.
        setExportTimelineYears([]);
        setExportIncludeAllTimelineRows(false);
        setExportGanttAssignee(EXPORT_GANTT_ALL);
        setExportGanttSort(EXPORT_GANTT_SORT_OWNER);
        setExportGanttOpen(true);
    };

    const buildExportGanttParams = () => {
        const params = new URLSearchParams();
        exportProjectStatuses.forEach((s) => params.append('project_statuses[]', s));
        exportTaskStatuses.forEach((s) => params.append('task_statuses[]', s));
        exportTimelineYears.forEach((y) => params.append('timeline_year[]', y));
        if (exportIncludeAllTimelineRows) {
            params.append('include_all_timeline_rows', '1');
        }
        if (exportGanttAssignee !== EXPORT_GANTT_ALL) {
            params.append('assigned_to', exportGanttAssignee);
        }
        params.append('sort', exportGanttSort);
        return params;
    };

    const handleExportGanttDownload = () => {
        window.open(`${route('projects.export-gantt')}?${buildExportGanttParams().toString()}`, '_blank', 'noopener,noreferrer');
        setExportGanttOpen(false);
    };

    const columnFilterOptions = useMemo(
        () => projectFilterOptions({ t, statuses, users, companies: companyOptions, years }),
        [t, statuses, users, companyOptions, years],
    );

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        setSortColumn(column);
        setSortDirection(direction);
        // `created_at` is the server's own fallback for an unrecognised key.
        applyFilters({
            sort_by: serverSortKey(projectTableDescriptor, column) ?? 'created_at',
            sort_order: direction,
            page: 1,
        });
    };

    const clearSort = () => {
        setSortColumn(null);
        setSortDirection(null);
        applyFilters({
            sort_by: undefined,
            sort_order: undefined,
            page: 1,
        });
    };

    const clearFilters = () => {
        setSearchValue('');
        // timeline_year resets to the current year via its spec default; the date range
        // is page-local so it has to be cleared explicitly.
        clearServerFilters({ date_from: undefined, date_to: undefined });
    };

    // Inline editing handlers. Without projects.status.change the cell renders
    // read-only, so the handler is never wired up in the first place.
    const handleUpdate = {
        status: async (projectId: string, value: string) => {
            router.patch(
                `/projects/${projectId}/status`,
                {
                    status: value,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('projects.index.messages.status_updated', 'Status updated'));
                    },
                    onError: () => {
                        toast.error(t('projects.index.messages.failed_update_status', 'Failed to update status'));
                    },
                },
            );
        },
    };

    const paginationMeta = useMemo<PaginationMeta>(() => {
        return {
            current_page: pagination.current_page ?? 1,
            last_page: pagination.last_page ?? 1,
            per_page: pagination.per_page ?? 25,
            total: pagination.total ?? projects.length,
            from: pagination.from ?? null,
            to: pagination.to ?? null,
        };
    }, [pagination, projects.length]);

    const ganttPaginationMeta = useMemo<PaginationMeta>(() => {
        const { total, current_page, per_page, last_page } = ganttPortfolioMeta;
        const from = total === 0 ? 0 : (current_page - 1) * per_page + 1;
        const to = total === 0 ? 0 : Math.min(current_page * per_page, total);

        return {
            current_page,
            last_page,
            per_page,
            total,
            from,
            to,
        };
    }, [ganttPortfolioMeta]);

    const handlePerPageChange = (perPage: number) => {
        applyFilters({ per_page: perPage, page: 1 });
    };

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('projects.index.title', 'Projects')} />
            <PageHeader
                title={t('projects.index.heading', 'Projects')}
                subtitle={t('projects.index.subheading', 'Manage your projects and tasks')}
                actions={
                    <>
                        <Button type="button" variant="outline" onClick={openExportGanttDialog}>
                            <FileSpreadsheet className="mr-2 h-4 w-4" />
                            {t('projects.index.export_gantt.button', 'Export Gantt')}
                        </Button>
                        {/* Gated: engineers hold projects.read but not projects.create,
                            and the route already refuses them — the button should say so. */}
                        <CreateButton
                            href="/projects/create"
                            permission="projects.create"
                            label={t('projects.index.actions.create', 'Create Project')}
                        />
                    </>
                }
            />

            <PageContent>
                <GenericToolbar
                    quickFilterButtonLabel={quickFilterButtonLabel(projectTableDescriptor, t, columnFilters)}
                    quickFilterDefinitions={definitions.quickFilterDefinitions}
                    columnDefinitions={definitions.columnDefinitions}
                    extraFilterDefinitions={definitions.extraFilterDefinitions}
                    columnFilterOptions={columnFilterOptions}
                    columnFilterLoading={columnFilterLoading}
                    columnFilters={columnFilters}
                    columnFilterSearch={columnFilterSearch}
                    setColumnFilterSearch={setColumnFilterSearch}
                    toggleSingleFilter={toggleSingleFilter}
                    handleFilterChange={handleFilterChange}
                    clearFilters={clearFilters}
                    isFilterOpen={isFilterOpen}
                    setIsFilterOpen={setIsFilterOpen}
                    isSortOpen={isSortOpen}
                    setIsSortOpen={setIsSortOpen}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    handleSortChange={handleSortChange}
                    clearSort={clearSort}
                    search={searchValue}
                    setSearch={setSearchValue}
                    perPage={paginationMeta.per_page}
                    onPerPageChange={handlePerPageChange}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    visibleColumns={visibleColumns}
                    handleColumnShow={handleColumnShow}
                    handleColumnHide={handleColumnHide}
                    filteredCount={viewMode === 'gantt' ? ganttPortfolioMeta.total : paginationMeta.total}
                    resourceName={projectTableDescriptor.resourceName}
                    resourceNamePlural={projectTableDescriptor.resourceNamePlural}
                    sortableColumns={viewMode === 'gantt' ? undefined : definitions.sortableColumns}
                    excludeFromMoreFilters={definitions.excludeFromMoreFilters}
                    showMoreFilters={definitions.showMoreFilters}
                    showGanttViewToggle
                    showPerPageSelect={viewMode !== 'gantt'}
                    inlineFilters={
                        viewMode === 'gantt' ? (
                            // The Gantt draws one 12-month timeline per project, so it can only honour a
                            // single year. Offering "All"/multi-select here would advertise a selection the
                            // chart silently drops down to its first year.
                            <Select value={selectedTimelineYear} onValueChange={(value) => handleFilterChange('timeline_year', [value])}>
                                <SelectTrigger
                                    aria-label={t('projects.index.filters.timeline_year', 'Project year')}
                                    className="border-primary/40 bg-background text-foreground hover:bg-accent hover:text-accent-foreground h-9 w-auto min-w-[140px] gap-2 rounded-md px-3 font-medium"
                                >
                                    <CalendarRange className="text-primary h-4 w-4" />
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {timelineYearOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        aria-label={t('projects.index.filters.timeline_year', 'Project year')}
                                        title={timelineYearButtonLabel}
                                        className="border-primary/40 bg-background text-foreground hover:bg-accent hover:text-accent-foreground h-9 w-auto min-w-[140px] justify-start gap-2 rounded-md px-3 font-medium"
                                    >
                                        <CalendarRange className="text-primary h-4 w-4 shrink-0" />
                                        <span className="max-w-[160px] truncate">{timelineYearButtonLabel}</span>
                                        <ChevronDown className="ml-auto h-4 w-4 shrink-0 opacity-60" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-44">
                                    <DropdownMenuCheckboxItem
                                        checked={selectedTimelineYears.length === 0}
                                        onSelect={(event) => event.preventDefault()}
                                        onCheckedChange={() => handleFilterChange('timeline_year', [])}
                                    >
                                        {t('projects.index.filters.all_years', 'All')}
                                    </DropdownMenuCheckboxItem>
                                    <DropdownMenuSeparator />
                                    {timelineYearOptions.map((option) => (
                                        <DropdownMenuCheckboxItem
                                            key={option.value}
                                            checked={selectedTimelineYears.includes(option.value)}
                                            onSelect={(event) => event.preventDefault()}
                                            onCheckedChange={() => toggleSingleFilter('timeline_year', option.value)}
                                        >
                                            {option.label}
                                        </DropdownMenuCheckboxItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )
                    }
                />

                {viewMode === 'gantt' ? (
                    <div className="bg-card flex min-h-0 flex-1 flex-col gap-0 overflow-hidden rounded-xl border">
                        <div className="bg-muted/25 dark:bg-muted/15 border-b px-4 py-4">
                            <p className="text-sm font-semibold tracking-tight">
                                {t('projects.index.portfolio_gantt.filters_heading', 'Gantt filters')}
                            </p>
                            <p className="text-muted-foreground mt-1 text-xs">
                                {t(
                                    'projects.index.portfolio_gantt.filters_subheading',
                                    'Refine which projects appear, then scroll each timeline like on the project page.',
                                )}
                            </p>
                            <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-4">
                                <div className="flex flex-col gap-1.5">
                                    <Label className="text-muted-foreground text-xs">
                                        {t('projects.index.portfolio_gantt.project_statuses', 'Project statuses')}
                                    </Label>
                                    <MultiSelect
                                        options={ganttProjectStatusMultiOptions}
                                        selected={ganttProjectStatusesFilter}
                                        onChange={setGanttProjectStatusesFilter}
                                        placeholder={t('projects.index.export_gantt.project_statuses_placeholder', 'Select project statuses…')}
                                        displayMode="count"
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <Label className="text-muted-foreground text-xs">
                                        {t('projects.index.portfolio_gantt.task_statuses', 'Task statuses')}
                                    </Label>
                                    <MultiSelect
                                        options={ganttTaskStatusMultiOptions}
                                        selected={ganttTaskStatusesFilter}
                                        onChange={setGanttTaskStatusesFilter}
                                        placeholder={t('projects.index.export_gantt.task_statuses_placeholder', 'Select task statuses…')}
                                        displayMode="count"
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <Label className="text-muted-foreground text-xs">
                                        {t('projects.index.portfolio_gantt.country_types', 'Country types')}
                                    </Label>
                                    <MultiSelect
                                        options={ganttCountryTypeMultiOptions}
                                        selected={ganttCountryTypesFilter}
                                        onChange={setGanttCountryTypesFilter}
                                        placeholder={t('projects.tables.country_type', 'Country Type')}
                                        displayMode="count"
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <Label className="text-muted-foreground text-xs">
                                        {t('projects.index.portfolio_gantt.assignees', 'Assignees')}
                                    </Label>
                                    <MultiSelect
                                        options={ganttAssigneeMultiOptions}
                                        selected={ganttAssigneesFilter}
                                        onChange={setGanttAssigneesFilter}
                                        placeholder={t('projects.index.export_gantt.assignee_all', 'Everyone')}
                                        displayMode="count"
                                    />
                                </div>
                            </div>
                        </div>
                        {ganttPortfolioError ? <p className="text-destructive px-4 py-2 text-sm">{ganttPortfolioError}</p> : null}
                        {!hasConfiguredGanttFilters ? (
                            <p className="text-muted-foreground px-4 py-10 text-center text-sm">
                                {t('projects.index.portfolio_gantt.setup_filters', 'Select at least one Gantt filter to show charts.')}
                            </p>
                        ) : null}
                        {hasConfiguredGanttFilters && ganttPortfolioLoading && ganttPortfolioProjects.length === 0 ? (
                            <p className="text-muted-foreground px-4 py-3 text-sm">
                                {t('projects.index.gantt.loading_subtitle', 'Preparing your project timeline...')}
                            </p>
                        ) : null}
                        {hasConfiguredGanttFilters && !ganttPortfolioLoading && ganttPortfolioProjects.length === 0 && !ganttPortfolioError ? (
                            <p className="text-muted-foreground px-4 py-10 text-center text-sm">
                                {t('projects.index.portfolio_gantt.empty', 'No projects match the selected filters.')}
                            </p>
                        ) : null}
                        {ganttPortfolioProjects.length > 0 ? (
                            <div className="flex flex-col gap-4 px-4 py-4">
                                {ganttPortfolioProjects.map((project) => (
                                    <PortfolioProjectGanttCard key={project.id} project={project} timelineYear={Number(selectedTimelineYear)} />
                                ))}
                            </div>
                        ) : null}
                        {viewMode === 'gantt' && hasConfiguredGanttFilters && ganttPortfolioMeta.total > 0 ? (
                            <div className="bg-muted/15 dark:bg-muted/10 border-t px-2 py-3">
                                <PaginationControls
                                    pagination={ganttPaginationMeta}
                                    onPageChange={setGanttPortfolioPage}
                                    loading={ganttPortfolioLoading}
                                />
                            </div>
                        ) : null}
                    </div>
                ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {projects.map((project) => (
                            <ProjectCard key={project.id} project={project} />
                        ))}
                    </div>
                ) : (
                    <div>
                        <ProjectTable
                            projects={projects}
                            onUpdate={handleUpdate}
                            readOnly={!canChangeStatus}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            onSortChange={handleSortChange}
                            onColumnHide={handleColumnHide}
                            onColumnShow={handleColumnShow}
                            visibleColumns={visibleColumns}
                            columnFilters={columnFilters}
                            columnFilterOptions={columnFilterOptions}
                            onFilterChange={handleFilterChange}
                            statuses={statuses}
                        />
                    </div>
                )}

                {viewMode === 'grid' && projects.length === 0 && (
                    <div className="py-12 text-center">
                        <p className="text-muted-foreground">
                            {t('projects.index.empty.no_projects_found', 'No projects found matching your filters.')}
                        </p>
                    </div>
                )}

                {viewMode !== 'gantt' ? (
                    <PaginationControls
                        pagination={paginationMeta}
                        onPageChange={(page) => applyFilters({ page })}
                        onPerPageChange={handlePerPageChange}
                        loading={loading}
                    />
                ) : null}

                <ResponsiveModal open={exportGanttOpen} onOpenChange={setExportGanttOpen} variant="sheet">
                    <ResponsiveModalContent size="lg">
                        <ResponsiveModalHeader>
                            <ResponsiveModalTitle>{t('projects.index.export_gantt.dialog_title', 'Export Gantt to Excel')}</ResponsiveModalTitle>
                            <ResponsiveModalDescription>
                                {t(
                                    'projects.index.export_gantt.dialog_description',
                                    'Select which project and task statuses to include. The spreadsheet covers 15 days before today through 45 days ahead and is laid out for A4 landscape printing.',
                                )}
                            </ResponsiveModalDescription>
                        </ResponsiveModalHeader>
                        <ResponsiveModalBody className="flex flex-col gap-4">
                            <div className="flex flex-col gap-2">
                                <span className="text-sm font-medium">{t('projects.index.export_gantt.timeline_year', 'Project year')}</span>
                                <MultiSelect
                                    options={timelineYearOptions}
                                    selected={exportTimelineYears}
                                    onChange={setExportTimelineYears}
                                    placeholder={t('projects.index.export_gantt.timeline_year_all', 'All years included')}
                                />
                                <p className="text-muted-foreground text-xs">
                                    {t(
                                        'projects.index.export_gantt.timeline_year_hint',
                                        'Leave empty to include all years. Otherwise only projects whose start or end date falls in one of the selected years are included (same as the projects list).',
                                    )}
                                </p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className="text-sm font-medium">{t('projects.index.export_gantt.assignee_filter', 'Tasks assigned to')}</span>
                                <Select value={exportGanttAssignee} onValueChange={setExportGanttAssignee}>
                                    <SelectTrigger className="h-9 w-full rounded-md">
                                        <SelectValue placeholder={t('projects.index.export_gantt.assignee_all', 'Everyone')} />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl">
                                        {exportGanttAssigneeOptions.map((option) => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-muted-foreground text-xs">
                                    {t(
                                        'projects.index.export_gantt.assignee_filter_hint',
                                        'Export only tasks assigned to this person. Each project row is included only if it has at least one matching task. Project year, statuses, and timeline options above still apply.',
                                    )}
                                </p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className="text-sm font-medium">{t('projects.index.export_gantt.sort_by', 'Sort projects by')}</span>
                                <Select value={exportGanttSort} onValueChange={setExportGanttSort}>
                                    <SelectTrigger className="h-9 w-full rounded-md">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl">
                                        <SelectItem value={EXPORT_GANTT_SORT_OWNER}>
                                            {t('projects.index.export_gantt.sort_by_owner', 'Assigned user (A–Z), then project number')}
                                        </SelectItem>
                                        <SelectItem value={EXPORT_GANTT_SORT_PROJECT_NUMBER}>
                                            {t('projects.index.export_gantt.sort_by_project_number', 'Project number')}
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-muted-foreground text-xs">
                                    {t('projects.index.export_gantt.sort_by_hint', 'Choose how project rows are ordered in the spreadsheet.')}
                                </p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="flex items-start gap-3">
                                    <Checkbox
                                        id="export-include-all-timeline-rows"
                                        checked={exportIncludeAllTimelineRows}
                                        onCheckedChange={(v) => setExportIncludeAllTimelineRows(v === true)}
                                    />
                                    <div className="grid gap-1.5 leading-none">
                                        <Label
                                            htmlFor="export-include-all-timeline-rows"
                                            className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                        >
                                            {t('projects.index.export_gantt.include_all_rows', 'Include tasks outside the export window')}
                                        </Label>
                                        <p className="text-muted-foreground text-xs">
                                            {t(
                                                'projects.index.export_gantt.include_all_rows_hint',
                                                'Active and on-hold projects are always listed so you can see live work that falls outside the timeline; completed and cancelled projects appear only when they overlap it. This option controls tasks: when checked, tasks outside the export window (15 days before today through 45 days ahead) are also shown; when unchecked, only tasks overlapping that window are drawn.',
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className="text-sm font-medium">{t('projects.index.export_gantt.project_statuses', 'Project statuses')}</span>
                                <MultiSelect
                                    options={projectStatusExportOptions}
                                    selected={exportProjectStatuses}
                                    onChange={setExportProjectStatuses}
                                    placeholder={t('projects.index.export_gantt.project_statuses_placeholder', 'Select project statuses…')}
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className="text-sm font-medium">{t('projects.index.export_gantt.task_statuses', 'Task statuses')}</span>
                                <MultiSelect
                                    options={taskStatusExportOptions}
                                    selected={exportTaskStatuses}
                                    onChange={setExportTaskStatuses}
                                    placeholder={t('projects.index.export_gantt.task_statuses_placeholder', 'Select task statuses…')}
                                />
                            </div>
                        </ResponsiveModalBody>
                        <ResponsiveModalFooter>
                            <Button type="button" variant="outline" onClick={() => setExportGanttOpen(false)}>
                                {t('projects.index.export_gantt.cancel', 'Cancel')}
                            </Button>
                            <Button type="button" onClick={handleExportGanttDownload}>
                                {t('projects.index.export_gantt.download', 'Download')}
                            </Button>
                        </ResponsiveModalFooter>
                    </ResponsiveModalContent>
                </ResponsiveModal>
            </PageContent>
        </AppLayout>
    );
}
